import { useEffect, useState } from "react"
import { useNavigate, useLocation, useSearchParams } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import { createDevShortcutSession } from "~/api/assessmentSessions"
import type { AssessmentSessionResponse } from "~/api/assessmentSessions"
import { PageShell } from "~/components/PageShell"
import { assetUrl } from "~/utils/assetUrl"

const MIN_PROCESSING_DISPLAY_MS = 8500

const PHRASE_INTERVAL_MS = 2000

const phraseFade = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
}

export function ComputingResultsPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const state = location.state as { sessionToken?: string; result?: unknown } | null
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0)
  const [devError, setDevError] = useState<string | null>(null)
  const result = state?.result as { dog_name?: string } | undefined

  const devType = import.meta.env.DEV && !state ? searchParams.get("type") : null
  const dogName =
    result?.dog_name?.trim() || searchParams.get("name")?.trim() || "your dog"
  const phrases = [
    `Compiling ${dogName}'s responses…`,
    `Reading ${dogName}'s personality signals…`,
    "This is where things get interesting…",
    `Building ${dogName}'s Doggy Dynamo profile…`,
    "Almost ready to reveal…",
  ]

  useEffect(() => {
    let cancelled = false

    if (devType) {
      let minDisplayElapsed = false
      let devResult: AssessmentSessionResponse | null = null

      const maybeNavigate = () => {
        if (cancelled || !minDisplayElapsed || !devResult) return
        navigate("/assessment/reveal", {
          state: { sessionToken: devResult.session_token, result: devResult },
          replace: true,
        })
      }

      const minTimer = setTimeout(() => {
        minDisplayElapsed = true
        maybeNavigate()
      }, MIN_PROCESSING_DISPLAY_MS)

      createDevShortcutSession(devType, searchParams.get("name") ?? undefined)
        .then((result) => {
          devResult = result
          maybeNavigate()
        })
        .catch((err) => {
          if (!cancelled) {
            setDevError(err instanceof Error ? err.message : "Dev shortcut failed")
          }
        })

      return () => {
        cancelled = true
        clearTimeout(minTimer)
      }
    }

    const t = setTimeout(() => {
      if (!cancelled) {
        navigate("/assessment/reveal", { state: state ?? undefined, replace: true })
      }
    }, MIN_PROCESSING_DISPLAY_MS)

    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [navigate, state, devType, searchParams])

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length)
    }, PHRASE_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [phrases.length])

  return (
    <PageShell centerContent>
      <motion.div
        className="flex flex-col items-center gap-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        <div className="relative flex items-center justify-center">
          <motion.div
            className="absolute inset-0 rounded-full border-2 border-[var(--color-gold-400)]"
            animate={{
              scale: [1, 1.4, 1],
              opacity: [0.5, 0, 0.5],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
          <motion.div
            className="relative flex h-28 w-28 items-center justify-center"
            animate={{ rotate: 360 }}
            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
          >
            <img
              src={assetUrl("/images/swish.png")}
              alt=""
              className="h-full w-full object-contain"
              aria-hidden
            />
          </motion.div>
        </div>

        <div className="flex min-h-[3rem] items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.p
              key={currentPhraseIndex}
              variants={phraseFade}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.4 }}
              className="text-center text-lg font-medium text-[var(--color-text-primary)]"
            >
              {phrases[currentPhraseIndex]}
            </motion.p>
          </AnimatePresence>
        </div>

        <p className="text-sm text-[var(--color-text-secondary)]">
          {dogName}&apos;s DoggyDynamo profile is almost ready.
        </p>
        {devError && (
          <p className="text-sm text-red-600" role="alert">
            {devError}
          </p>
        )}
      </motion.div>
    </PageShell>
  )
}
